import { Capability } from 'homey-lib/webpack/index.js';
import { useEffect, useMemo, useRef, useState } from 'react';

import { DeviceSettings } from '../../config/DeviceSettings';
import { zoneActivityCapabilities } from '../../config/zoneActivityCapabilities';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useSensorState } from '../../hooks/useSensorState';

import { theme } from '../../theme/theme';
import styles from './IndicatorAlarm.module.css';

const ACTIVE_ALARM_COLOR = '#ff8400';
const ACTIVE_ALARM_BACKGROUND_COLOR = '#ff84001a';
const ALARM_CYCLE_INTERVAL = 2000;
const alarmCycleSubscribers = new Set();

let alarmCycleIntervalId = null;

export function IndicatorAlarm(props) {
  const { i18n } = useI18n();
  const { values } = useSensorState({
    deviceId: props.device.id,
    capabilitiesObj: props.device.capabilitiesObj,
  });
  const zoneActivityEnabled = props.device.settings[DeviceSettings.ZoneActivityDisabled] === false;
  const alarmCapabilityIdSet = useMemo(() => {
    if (props.alarmCapabilityIds == null) {
      return null;
    }

    return new Set(props.alarmCapabilityIds);
  }, [props.alarmCapabilityIds]);
  const alarmCapabilityEntries = useMemo(() => {
    return Object.entries(props.device.capabilitiesObj ?? {}).filter(([capabilityId]) => {
      if (Capability.getBaseId(capabilityId).startsWith('alarm_') !== true) {
        return false;
      }

      if (alarmCapabilityIdSet != null && alarmCapabilityIdSet.has(capabilityId) !== true) {
        return false;
      }

      return true;
    });
  }, [alarmCapabilityIdSet, props.device.capabilitiesObj]);
  const activeAlarmStatuses = useMemo(() => {
    return getActiveAlarmStatuses({
      alarmCapabilityEntries,
      i18n,
      values,
      zoneActivityEnabled,
    });
  }, [alarmCapabilityEntries, i18n, values, zoneActivityEnabled]);
  const activeAlarmStatusSignature = useMemo(() => {
    return activeAlarmStatuses
      .map((alarmStatus) => {
        return `${alarmStatus.id}:${alarmStatus.label}`;
      })
      .join('|');
  }, [activeAlarmStatuses]);
  const [currentAlarmIndex, setCurrentAlarmIndex] = useState(0);

  useEffect(() => {
    setCurrentAlarmIndex(0);
  }, [activeAlarmStatusSignature]);

  useEffect(() => {
    if (currentAlarmIndex < activeAlarmStatuses.length) {
      return;
    }

    setCurrentAlarmIndex(0);
  }, [activeAlarmStatuses.length, currentAlarmIndex]);

  useAlarmCycleTick({
    enabled: activeAlarmStatuses.length > 1,
    onTick() {
      setCurrentAlarmIndex((currentIndex) => {
        return (currentIndex + 1) % activeAlarmStatuses.length;
      });
    },
  });

  const alarmStatus = activeAlarmStatuses[currentAlarmIndex] ?? activeAlarmStatuses[0];

  if (alarmStatus == null) {
    return null;
  }

  if (alarmStatus.showIcon === true) {
    return (
      <div
        className={styles.root}
        style={{
          '--prop-icon-background-color': alarmStatus.backgroundColor,
          '--prop-icon-color': alarmStatus.color,
          '--prop-text-color': alarmStatus.color,
        }}
      >
        <span className={styles.icon} data-tone={alarmStatus.tone} />
        <div className={styles.statusText}>{alarmStatus.label}</div>
      </div>
    );
  }

  return (
    <div className={styles.root} style={{ '--prop-text-color': alarmStatus.color }}>
      <div className={styles.statusText}>{alarmStatus.label}</div>
    </div>
  );
}

function getActiveAlarmStatuses(props) {
  if (props.alarmCapabilityEntries.length === 0) {
    return [];
  }

  const alarmStatuses = [];

  for (const [capabilityId, capabilityObj] of props.alarmCapabilityEntries) {
    if (props.values[capabilityId] !== true) {
      continue;
    }

    const isZoneActivityAlarm =
      props.zoneActivityEnabled === true &&
      zoneActivityCapabilities.has(Capability.getBaseId(capabilityId));
    const tone =
      isZoneActivityAlarm === true
        ? {
            backgroundColor: theme.color.blue_o_10,
            color: theme.color.highlight,
            name: 'zone',
          }
        : getAlarmTone(capabilityId);

    alarmStatuses.push({
      backgroundColor: tone.backgroundColor,
      color: tone.color,
      id: capabilityId,
      label:
        capabilityObj.titleShort ??
        capabilityObj.title ??
        props.i18n.messageFormatter('device.alarm.active'),
      showIcon: true,
      tone: tone.name,
    });
  }

  alarmStatuses.sort((leftAlarmStatus, rightAlarmStatus) => {
    return getAlarmPriority(leftAlarmStatus.id) - getAlarmPriority(rightAlarmStatus.id);
  });

  return alarmStatuses;
}

function getAlarmPriority(capabilityId) {
  const baseCapabilityId = Capability.getBaseId(capabilityId);

  switch (baseCapabilityId) {
    case 'alarm_smoke':
      return 0;
    case 'alarm_fire':
      return 1;
    case 'alarm_co':
      return 2;
    case 'alarm_water':
      return 3;
    case 'alarm_heat':
      return 4;
    default:
      return 100;
  }
}

function getAlarmTone(capabilityId) {
  const baseCapabilityId = Capability.getBaseId(capabilityId);

  if (
    baseCapabilityId === 'alarm_smoke' ||
    baseCapabilityId === 'alarm_fire' ||
    baseCapabilityId === 'alarm_co'
  ) {
    return {
      backgroundColor: '#d81c1d1a',
      color: theme.color.error,
      name: 'danger',
    };
  }

  return {
    backgroundColor: ACTIVE_ALARM_BACKGROUND_COLOR,
    color: ACTIVE_ALARM_COLOR,
    name: 'alarm',
  };
}

function useAlarmCycleTick(props) {
  const latestOnTickRef = useRef(props.onTick);

  useEffect(() => {
    latestOnTickRef.current = props.onTick;
  }, [props.onTick]);

  useEffect(() => {
    if (props.enabled !== true) {
      return undefined;
    }

    function handleTick() {
      latestOnTickRef.current();
    }

    return subscribeAlarmCycleTick(handleTick);
  }, [props.enabled]);
}

function subscribeAlarmCycleTick(onTick) {
  alarmCycleSubscribers.add(onTick);

  if (alarmCycleSubscribers.size === 1) {
    alarmCycleIntervalId = setInterval(() => {
      for (const subscriber of alarmCycleSubscribers) {
        subscriber();
      }
    }, ALARM_CYCLE_INTERVAL);
  }

  return () => {
    alarmCycleSubscribers.delete(onTick);

    if (alarmCycleSubscribers.size === 0 && alarmCycleIntervalId != null) {
      clearInterval(alarmCycleIntervalId);
      alarmCycleIntervalId = null;
    }
  };
}
